import { z } from 'zod';
import { BillingIntervalEnum, BillingIntervalSchema, } from '../types/subscriptions/enums';
import { formatMoneyCents, PublicExperienceCurrencySchema } from './offer';
import { BillingPolicySchema, describeBillingPolicy } from './billing-schedule';
/** Authoring metadata only. Never customer acceptance or a billing instruction. */
export const PaymentTermsBasisSchema = z
    .object({
    billingPolicy: BillingPolicySchema.optional(),
    businessName: z.string().trim().min(1).max(200),
    amountCents: z.number().int().positive(),
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    cancellationContact: z.string().trim().min(1).max(320),
})
    .strict();
export const PaymentTermsAuthoringSchema = z
    .object({
    cancellationContact: z.string().trim().max(320),
    reviewedFor: PaymentTermsBasisSchema.nullable(),
})
    .strict();
export const PXE_PAYMENT_TERMS_VERSION = 'pxe-payment-terms-v2';
/** Called only for a new form or an explicit reset. No duration is implied. */
export function buildDefaultPaymentTerms(input) {
    const basis = PaymentTermsBasisSchema.parse(input);
    if (basis.billingPolicy?.kind === 'CALENDAR') {
        const amount = formatMoneyCents(basis.amountCents, basis.currency);
        const schedule = describeBillingPolicy(basis.billingPolicy);
        const start = basis.billingPolicy.initialChargeMode === 'IMMEDIATE'
            ? `charge ${amount} today and again on the next scheduled billing date shown at checkout. These full payments may be close together`
            : `save my payment method today without collecting a payment and begin charging ${amount} on the first scheduled billing date shown at checkout`;
        return `I authorize ${basis.businessName} to ${start}. Recurring payments of ${amount} continue ${schedule} until I cancel. To cancel future payments, contact ${basis.cancellationContact}.`;
    }
    const units = {
        [BillingIntervalEnum.DAILY]: 'day',
        [BillingIntervalEnum.WEEKLY]: 'week',
        [BillingIntervalEnum.MONTHLY]: 'month',
        [BillingIntervalEnum.YEARLY]: 'year',
    };
    const unit = units[basis.interval];
    const recurrence = basis.intervalCount === 1
        ? `every ${unit}`
        : `every ${basis.intervalCount} ${unit}s`;
    return `I authorize ${basis.businessName} to charge ${formatMoneyCents(basis.amountCents, basis.currency)} ${recurrence} to the payment method I provide. Payments will continue until I cancel. To cancel future payments, contact ${basis.cancellationContact}.`;
}
export function paymentTermsNeedReview(reviewedFor, current) {
    return (!reviewedFor ||
        [...new Set([...Object.keys(current), ...Object.keys(reviewedFor)])].some((key) => JSON.stringify(reviewedFor[key]) !==
            JSON.stringify(current[key])));
}
